import { Contract, ethers } from "ethers";
import { useEffect, useMemo, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useAccount, useBalance, useContractRead, useSigner } from "wagmi";

import {
  contractAddressSelector,
  pendingEthTxAtom,
  tokenBalanceAtomFamily,
  waitingOnMetaMaskAtom,
} from "../../atoms";
import Amp from "../../contracts/Amp.abi.json";
import FlexaCollateralManager from "../../contracts/FlexaCollateralManager.abi.json";
import Staking from "../../contracts/Staking";
import { useConfig } from "../config";
import Flexacoin from "../flexacoin";

const coinbaseWalletLogo = "/assets/img/connect-coinbase.png";
const metamaskLogo = "/assets/img/connect-metamask.png";

const abis = {
  amp: JSON.stringify(Amp.abi),
  flexaCollateralManager: JSON.stringify(FlexaCollateralManager),
  fxc: Flexacoin.abi,
  staking: Staking.abi,
};

const getAbi = (key: string) => {
  const abi: (typeof abis)[keyof typeof abis] | undefined = abis[key];

  if (!abi) {
    throw new Error(`No ABI found for contract: ${key}`);
  }

  return abi;
};

export const getContract = (key: string, address: string) => {
  const abi = getAbi(key);
  return new Contract(address, abi);
};

export const useContract = (token: string) => {
  const { contracts } = useConfig();

  return useMemo(() => {
    if (!contracts?.[token]) {
      return null;
    }
    return getContract(token, contracts[token]);
  }, [contracts?.[token]]);
};

const useContractInfo = (token: string) => {
  const { contracts } = useConfig();

  return useMemo(() => {
    if (!contracts?.[token]) {
      return null;
    }
    return {
      address: contracts[token],
      abi: getAbi(token),
    };
  }, [contracts?.[token]]);
};

export const useFXCContract = () => useContract("fxc");

export const useStakingContract = () => useContract("staking");

export const useAmpContract = () => useContract("amp");

export const useFCMContract = () => useContract("flexaCollateralManager");

const replaceItemAtIndex = <T>(arr: T[], index: number, newValue: T) => [
  ...arr.slice(0, index),
  newValue,
  ...arr.slice(index + 1),
];

export const removeItemAtIndex = <T>(arr: T[], index: number) => [
  ...arr.slice(0, index),
  ...arr.slice(index + 1),
];

interface EtherCallOptions {
  watchPendingTransction?: boolean;
  confirmations?: number;
  watchKey?: string;
}

const defaultOptions = {
  watchPendingTransction: true,
  confirmations: 8,
};

export const useEtherCall = (
  contract: Contract,
  options?: EtherCallOptions
) => {
  const setWaitingOnMetaMask = useSetRecoilState(waitingOnMetaMaskAtom);
  const setPendingEthTx = useSetRecoilState(pendingEthTxAtom);
  const { data: signer } = useSigner();
  const { address: account } = useAccount();

  options = {
    ...defaultOptions,
    ...options,
  };

  return async (cb) => {
    setWaitingOnMetaMask(true);

    try {
      console.log(`contract.connect(signer`, signer);
      const tx = await cb(contract.connect(signer), account);
      console.log(`contract.connect(signer)`, signer);

      if (options.watchPendingTransction) {
        setPendingEthTx((state) => {
          const newTx = {
            ...tx,
            requiredConfirmations: options.confirmations,
            confirmed: false,
            watchKey: options.watchKey,
          };
          const idx = state.findIndex(
            (pendingTx) => pendingTx.hash === tx.hash
          );
          if (idx >= 0) {
            state[idx] = newTx;
          } else {
            state = [...state, newTx];
          }
          return state;
        });

        for (let i = 1; i <= options.confirmations; i++) {
          tx.wait(i).then((tx) => {
            setPendingEthTx((state) => {
              const idx = state.findIndex(
                (pendingTx) => pendingTx.hash === tx.transactionHash
              );

              if (idx < 0) {
                return state; // Not present
              }

              return replaceItemAtIndex(state, idx, {
                ...state[idx],
                confirmations: i,
                requiredConfirmations: options.confirmations,
                confirmed: i === options.confirmations,
              });
            });
          });
        }
      }

      return { tx };
    } catch (error) {
      console.error(error);
      return { error };
    } finally {
      setWaitingOnMetaMask(false);
    }
  };
};

export const useConnectorMeta = () => {
  const { connector } = useAccount();
  const [walletLogo, setWalletLogo] = useState("");
  const [walletName, setWalletName] = useState("");

  useEffect(() => {
    void (async () => {
      const provider = await connector?.getProvider();

      if (provider?.isMetaMask) {
        setWalletLogo(metamaskLogo);
        setWalletName("MetaMask");
      } else if (provider?.isWalletConnect) {
        setWalletLogo(provider.walletMeta?.icons?.[0] ?? "");
        setWalletName(provider.walletMeta?.name ?? "");
      } else {
        setWalletLogo(coinbaseWalletLogo);
        setWalletName("Coinbase Wallet");
      }
    })();
  }, [connector]);

  return [walletName, walletLogo];
};

const useTokenBalanceUpdater = (token: string) => {
  const { address } = useAccount();
  const contract = useRecoilValue(contractAddressSelector(token));
  const { data } = useBalance({ address, token: contract, watch: true });
  const balance = data?.value;

  const setTokenBalance = useSetRecoilState(tokenBalanceAtomFamily(token));

  useEffect(() => {
    if (balance) {
      setTokenBalance(balance);
    }
  }, [setTokenBalance, balance]);

  return null;
};

const useTokenBalanceByPartitionUpdater = (token: string) => {
  const { address: pubKey } = useAccount();
  const { abi, address } = useContractInfo(token);
  const { data } = useContractRead({
    address,
    // FIXME Shouldn't have to cast to `any`. `abi` was typed as `any` but, once it derived the correct type, everything here broke.
    abi: abi as any,
    functionName: "balanceOfByPartition",
    watch: true,
    select: (data) => data as unknown as ethers.BigNumber,
    args: [ethers.utils.formatBytes32String(""), pubKey],
  });

  const setTokenBalance = useSetRecoilState(tokenBalanceAtomFamily(token));

  useEffect(() => {
    if (data && data._isBigNumber) {
      setTokenBalance(data);
    }
  }, [setTokenBalance, data]);
};

export const useBalanceUpdater = () => {
  useTokenBalanceUpdater("fxc");
  useTokenBalanceByPartitionUpdater("amp");
};
